import React, { useState } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Row,
  Alert,
  InputGroup,
  Spinner
} from "react-bootstrap";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { initializeApp } from "firebase/app";
import { app } from "../../credenciales";
import style from "./AltaUsuario.module.css";
import Swal from 'sweetalert2';
import { generateQrCode, sendUserToFirestore } from "../../utils/auxiliarFunctions";
import { GoEye, GoEyeClosed } from "react-icons/go";

// Instancia secundaria para crear usuarios sin cerrar sesión del admin
const secondaryApp = initializeApp(app.options, "Secondary");
const secondaryAuth = getAuth(secondaryApp);

const AltaUsuario = ({ isModal = false, handleCloseModal }) => {
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    lastName: "",
    password: "",
    role: "1",
    numeroSocio: "",
    fechaNacimiento: "",
  });

  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const { email, name, lastName, password, role, numeroSocio, fechaNacimiento } = formData;

    if (!email || !name || !lastName || !password) {
      setError("Todos los campos obligatorios deben completarse.");
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres");
      setLoading(false);
      return;
    }

    try {
      // Crear usuario con la instancia secundaria (sin cerrar sesión del admin)
      await createUserWithEmailAndPassword(secondaryAuth, email, password);

      // Cerrar sesión del usuario secundario
      await signOut(secondaryAuth);

      // Generar QR
      const qrData = await generateQrCode(email);

      await sendUserToFirestore(email, name, lastName, numeroSocio, fechaNacimiento, role, qrData);

      Swal.fire({
        title: "Usuario creado exitosamente.",
        icon: "success"
      });
      setFormData({
        email: "",
        name: "",
        lastName: "",
        password: "",
        role: "1",
        numeroSocio: "",
        fechaNacimiento: "",
      });
      setLoading(false);
      handleCloseModal && handleCloseModal();
    } catch (err) {
      console.error("Error creando usuario:", err);
      if (err.code === "auth/email-already-in-use") {
        setError("Ya existe una cuenta con ese email");
        setLoading(false);
        return;
      }
      setError("Hubo un error al crear el usuario. Por favor, intente nuevamente.");
      setLoading(false);
    }
  };

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container className="py-4">
      <h1 className={`mb-4 titles text-center`}>Alta de usuario</h1>
      <Row className='d-flex justify-content-center'>
        <Col className={`${style.colContainer}`} md={6} sm={12}>
          <Form onSubmit={handleSubmit}>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="email">Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="name">Nombre</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="lastName">Apellido</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    id="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="fechaNacimiento">Fecha de nacimiento</Form.Label>
                  <Form.Control
                    type="date"
                    name="fechaNacimiento"
                    id="fechaNacimiento"
                    value={formData.fechaNacimiento}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="role">Rol</Form.Label>
                  <Form.Select
                    name="role"
                    id="role"
                    value={formData.role}
                    onChange={handleChange}
                  >
                    <option value="1">Admin</option>
                    <option value="2">Alumno</option>
                    <option value="3">Profesor</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="numeroSocio">Número de socio (opcional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="numeroSocio"
                    id="numeroSocio"
                    value={formData.numeroSocio}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="password">Contraseña</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      name="password"
                      id="password"
                      value={formData.password}
                      onChange={handleChange}
                    />
                    {showPassword ? (
                      <Button
                        className={style.showPswBtn}
                        variant="primary"
                        onClick={handleShowPassword}
                      >
                        <GoEye />
                      </Button>
                    ) : (
                      <Button
                        className={style.showPswBtn}
                        variant="primary"
                        onClick={handleShowPassword}
                      >
                        <GoEyeClosed />
                      </Button>
                    )}
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            {error && <Alert variant="danger">{error}</Alert>}

            {!isModal ? (
              <Button variant="primary" type="submit" className={`mt-3 ${style.sendBtn}`}>
                Crear usuario{" "}
                {loading && <Spinner as="span" size="sm" animation="grow" role="status" />}
              </Button>
            ) : (
              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button variant="secondary" onClick={handleCloseModal} className="secondaryBtn">Cancelar</Button>
                <Button variant="primary" type="submit"  className="primaryBtn">
                  Guardar cambios{" "}
                  {loading && <Spinner as="span" size="sm" animation="grow" role="status" />}
                </Button>
              </div>
            )}
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default AltaUsuario;
